import cmsConfig from "~/cmsConfig";
import type {  Response } from '~/types'
/**
 * 这个接口需要加上 /dev-api 或者 /prod-api 的, 它请求的是 java 的后端，
 * 在开发环境中，是通过 nuxt 的代理来访问 java 的后端，在生产环境中，是通过 nginx 的代理来访问 java 的后端
 * @param url 
 * @param Option 
 * @returns 
 */


const doUseHttp = async (url:string,options:any) => {
    const config = useRuntimeConfig()

    const javaApiPrefix = config.public.javaApiPrefix   //这个就是 /dev-api 或者 /prod-api 的
    const nuxtApp = useNuxtApp()

    let baseURL = ''
    if(url.startsWith('http') || options.nuxtServer === true){
        //当url是以http开头,说明需要直接访问外网，如果nuxtServer 为true，说明访问的是nuxt的/api的接口。这两种都不需要加上 /dev-api 或者 /prod-api 的
        baseURL = ''
    } else {
        // 其它情况才是返回转发到java后端,才需要加上 /dev-api 或者 /prod-api 
        baseURL = javaApiPrefix
    }

    // 统一前后端都使用 useFetch，这样首屏客户端会复用 SSR payload，不会重复向 Java 发起请求
    return useFetch(url, {
        baseURL: baseURL,
        ...options,

        // 请求拦截器：在客户端附加 token
        onRequest: ({ options: reqOptions }: { options: any }) => {
            let token: string = ''
            if (import.meta.client) {
                token = localStorage.getItem('token') || ''
            }

            reqOptions.headers = {
                Authorization: 'Bearer ' + token,
                ...reqOptions.headers,
                ...options.headers
            }
        },

        // 响应拦截器：统一处理 401
        onResponse: ({ response }: { response: any }) => {
            // 响应拦截器是没有返回值的，它的返回值不影响useFetch的返回值
            // 只有401 才需要在这里（onResponse）处理，因为这里是通过navigateTo 跳转的
            // 如果是404 或者是500 或者是200 中参数错误的，需要到then 中去处理，因为，它们是使用throw createError 的方式处理
            if (response.status === 401) {
                nuxtApp.runWithContext(() => {
                    navigateTo('/user/login')
                })
            }
        }
    })
}


function handleResOnServer<T>(url:string,res:any,resolve:any,reject:any){
    if(res.error?.value!=null && res.error?.value.statusCode === 404){
        throw createError({
            statusCode: 404,
            statusMessage:`请求地址${url}不存在!`,
            fatal: true
        })
    }else if(res.error?.value!=null && res.error?.value.statusCode === 500){
        throw createError({
            statusCode: 500,
            statusMessage:`请求地址${url} 出错,错误号：500!`,
            fatal: true
         })
    } else  if(res.data!=null && res.data?.value!=null && (res.data.value as any).code !== 200){
        // 这种情况是java 后台返回的错误信息，比如res.code =500 的错误。
        throw createError({
            statusCode: (res.data.value as any).code,
            statusMessage:`请求地址${url} 出错! , 错误信息：${(res.data.value as any).msg}`,
            fatal: true
        })
    }

    resolve(res.data.value as T)
}


function handleResOnClient<T>(url:string,res:any,resolve:any,reject:any){
    let error:Response<T> = {
        code:200,
        msg:'',
        data:{} as T
    }
    if(res.error?.value!=null && res.error?.value.statusCode === 404){
        error.code = 404
        error.msg = `请求地址${url}不存在!`
        reject(error)
    }else if(res.error?.value!=null && res.error?.value.statusCode === 500){
        error.code = 500
        error.msg = `请求地址${url} 出错,错误号：500!`
        reject(error)
    } else if(res.data!=null && res.data?.value!=null && (res.data.value as any).code !== 200){
        error.code = res.data.value.code
        error.msg =`${(res.data.value as any).msg}` 
       reject(error)
    }else{
        resolve(res.data.value as T)
    }
}

export const useHttp = <T>(url:string,options:any) => {
    return new Promise(async (resolve,reject)=>{
        try {
            const res = await doUseHttp(url, {
                ... options
            })
            
            if(import.meta.server){
                handleResOnServer<T>(url,res,resolve,reject)
            }else{
                handleResOnClient<T>(url,res,resolve,reject)
            }
        } catch (err) {
            reject(err)
        }
    })
}

/**
 * 使用 $fetch 的 HTTP 请求，避免数据序列化到 __NUXT_DATA__
 * 适用场景：需要减少 HTML payload，提高 text/html 比率的 SEO 优化
 */
const doUseHttpBy$Fetch = async (url:string,options:any) => {
    const config = useRuntimeConfig()
    const javaApiPrefix = config.public.javaApiPrefix
    const nuxtApp = useNuxtApp()

    // 构建完整 URL
    let fullUrl = ''
    if (url.startsWith('http') || options.nuxtServer === true) {
        // 完整 URL 或 nuxt 内部 API，直接使用
        fullUrl = url
    } else {
        // 需要代理到 Java 后端
        if (import.meta.server) {
            // 服务端：直接访问后端，绕过代理
            fullUrl = 'http://localhost:8080' + (url.startsWith('/') ? url : '/' + url)
        } else {
            // 客户端：使用代理路径
            fullUrl = javaApiPrefix + (url.startsWith('/') ? url : '/' + url)
        }
    }

    // 处理 headers
    let headers: any = {}
    if (import.meta.client) {
        let token: string = localStorage.getItem('token') || ''
        headers.Authorization = 'Bearer ' + token
    }
    headers = { ...headers, ...options.headers }

    // 使用 $fetch，避免序列化到 __NUXT_DATA__
    const response = await $fetch(fullUrl, {
        method: options.method || 'GET',
        headers: headers,
        body: options.body,
        ...options
    })

    // 处理 401 状态码
    if (response && (response as any).status === 401) {
        nuxtApp.runWithContext(() => {
            navigateTo('/user/login')
        })
    }

    return response
}

function handleResBy$FetchOnServer<T>(url:string,res:any,resolve:any){
    const statusCode = (res as any).code
    if (statusCode === 404){
        throw createError({
            statusCode: 404,
            statusMessage:`请求地址${url}不存在!`,
            fatal: true
        })
    } else if (statusCode === 500){
        throw createError({
            statusCode: 500,
            statusMessage:`请求地址${url} 出错,错误号：500!`,
            fatal: true
        })
    } else if (statusCode !== 200){
        throw createError({
            statusCode: statusCode,
            statusMessage:`请求地址${url} 出错! , 错误信息：${(res as any).msg}`,
            fatal: true
        })
    }

    resolve(res as T)
}

function handleResBy$FetchOnClient<T>(url:string,res:any,resolve:any,reject:any){
    const statusCode = (res as any).code
    let errorMsg = ''
    if (statusCode === 404){
        errorMsg = `请求地址${url}不存在!`
         alert(errorMsg)
        reject(errorMsg)
    } else if (statusCode === 500){
        errorMsg = `请求地址${url} 出错,错误号：500!`
         alert(errorMsg)
        reject(errorMsg)
    } else if (statusCode !== 200){
        errorMsg = `请求地址${url} 出错! , 错误信息：${(res as any).msg}`
         alert(errorMsg)
        reject(errorMsg)
    } else {
        resolve(res as T)
    }
}

/**
 * 使用 $fetch 的 HTTP 请求接口
 *
 * 特点：
 * - 数据不会被序列化到 __NUXT_DATA__，减少 HTML payload
 * - 适用于 SEO 优化场景，提高 text/html 比率
 * - 服务端直接访问后端，客户端使用代理
 *
 * @param url 请求路径，如 '/nuxt/article/list'
 * @param options 请求选项
 * @returns Promise<T>
 *
 * @example
 * ```typescript
 * const data = await useHttpBy$Fetch<Response<Page<Article>>>('/nuxt/article/list', {
 *     method: 'POST',
 *     body: { pageNum: 1, pageSize: 12 }
 * })
 * ```
 */
export const useHttpBy$Fetch = <T>(url:string,options:any) => {
    return new Promise(async (resolve,reject)=>{
        try {
            const res = await doUseHttpBy$Fetch(url, {
                ... options
            })

            if(import.meta.server){
                handleResBy$FetchOnServer<T>(url,res,resolve)
            }else{
                handleResBy$FetchOnClient<T>(url,res,resolve,reject)
            }
        } catch (err) {
            reject(err)
        }
    })
}
